// src/components/finances/PaymentForm.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  AttachMoney as CashIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Import custom components
import FileUploadComponent from '../../components/ui/FileUploadComponent';
import { Button } from '../../design-system/components/core';

const PaymentForm = ({ open, onClose, onSubmit, invoice }) => {
  // Default to today's date
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // Initial state for the payment form
  const [paymentData, setPaymentData] = useState({
    amount: invoice?.amount?.due || 0,
    date: today,
    method: 'bank_transfer',
    status: 'completed',
    reference: '',
    notes: '',
    attachment: null
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle file upload
  const handleFileUpload = (files) => {
    if (files && files.length > 0) {
      setPaymentData(prev => ({ 
        ...prev, 
        attachment: files[0] 
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!paymentData.amount || paymentData.amount <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!paymentData.date) {
      newErrors.date = 'Please select a date';
    }
    
    if (!paymentData.method) {
      newErrors.method = 'Please select a payment method';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submit
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(paymentData);
      onClose();
      // Reset form
      setPaymentData({
        amount: 0,
        date: today,
        method: 'bank_transfer',
        status: 'completed',
        reference: '',
        notes: '',
        attachment: null
      });
    } catch (error) {
      console.log('Error submitting payment:', error);
      // You could set an error message here
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency?.code || 'USD'
    }).format(amount);
  };
  
  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'bank_transfer':
        return <BankIcon />;
      case 'credit_card':
        return <CardIcon />;
      case 'cash':
        return <CashIcon />;
      default:
        return <PaymentIcon />;
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Record Payment
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {invoice && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Invoice #{invoice.invoiceNumber}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                <Typography variant="h6">{formatCurrency(invoice.amount?.total)}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Amount Paid</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(invoice.amount?.paid)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary">Amount Due</Typography>
                <Typography variant="h6" color={invoice.amount?.due > 0 ? 'warning.main' : 'success.main'}>
                  {formatCurrency(invoice.amount?.due)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Payment amount */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Payment Amount"
              name="amount"
              type="number"
              value={paymentData.amount}
              onChange={handleChange}
              fullWidth
              placeholder="Transaction ID, Check Number, etc."
            />
          </Grid>
          
          {/* Payment notes */}
          <Grid item xs={12}>
            <TextField
              label="Notes"
              name="notes"
              value={paymentData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Additional details about this payment"
            />
          </Grid>
        
          
          {/* Payment date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Payment Date"
              name="date"
              type="date"
              value={paymentData.date}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.date}
              helperText={errors.date}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          {/* Payment method */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.method}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                name="method"
                value={paymentData.method}
                onChange={handleChange}
                label="Payment Method"
              >
                <MenuItem value="bank_transfer">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BankIcon fontSize="small" sx={{ mr: 1 }} />
                    Bank Transfer
                  </Box>
                </MenuItem>
                <MenuItem value="credit_card">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CardIcon fontSize="small" sx={{ mr: 1 }} />
                    Credit Card
                  </Box>
                </MenuItem>
                <MenuItem value="cash">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CashIcon fontSize="small" sx={{ mr: 1 }} />
                    Cash
                  </Box>
                </MenuItem>
                <MenuItem value="check">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon fontSize="small" sx={{ mr: 1 }} />
                    Check
                  </Box>
                </MenuItem>
                <MenuItem value="other">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon fontSize="small" sx={{ mr: 1 }} />
                    Other
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Payment status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Payment Status</InputLabel>
              <Select
                name="status"
                value={paymentData.status}
                onChange={handleChange}
                label="Payment Status"
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Payment reference */}
          <Grid item xs={12}>
            <TextField
              label="Reference Number"
              name="reference"
              value={paymentData.reference}
              onChange={handleChange}
              fullWidth  
              required
              error={!!errors.amount}
              helperText={errors.amount}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {invoice?.currency?.symbol || '$'}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Payment receipt/attachment */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Receipt / Attachment (Optional)
            </Typography>
            <FileUploadComponent
              value={paymentData.attachment ? [paymentData.attachment] : []}
              onChange={(files) => handleFileUpload(files)}
              accept="image/*,application/pdf"
              multiple={false}
              buttonLabel="Upload Receipt"
              helperText="Upload payment receipt or confirmation"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          loading={isSubmitting}
          variant="contained" 
          
          magical={true}
        >
          Record Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentForm;
             